<template>
  <PrimeMultiSelect
    v-bind="mergedAttrs"
    :model-value="modelValue"
    :options="effectiveOptions"
    :option-label="optionLabel"
    :option-value="optionValue"
    @update:model-value="onUpdate"
  >
    <template v-if="showSelectAll" #option="slotProps">
      <span
        v-if="isSentinel(slotProps.option)"
        class="gpx-multiselect__select-all-row"
      >
        {{ anySelected ? 'Clear all' : 'Select all' }}
      </span>
      <slot v-else name="option" v-bind="slotProps">
        <span>{{ resolveOptionLabel(slotProps.option) }}</span>
      </slot>
    </template>
    <template
      v-for="(_, slotName) in forwardedSlots"
      :key="slotName"
      #[slotName]="slotData"
    >
      <slot :name="slotName" v-bind="slotData ?? {}" />
    </template>
  </PrimeMultiSelect>
</template>

<script>
export default { inheritAttrs: false }
</script>

<script setup>
import { computed, useAttrs, useSlots } from 'vue'
import PrimeMultiSelect from 'primevue/multiselect'

const SELECT_ALL_SENTINEL = '__gpx_select_all__'

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] },
  optionLabel: { type: [String, Function], default: 'label' },
  optionValue: { type: [String, Function], default: null },
  showSelectAll: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const attrs = useAttrs()
const slots = useSlots()

const sentinelOption = computed(() => {
  const labelKey =
    typeof props.optionLabel === 'string' ? props.optionLabel : null
  const valueKey =
    typeof props.optionValue === 'string' ? props.optionValue : null
  return {
    ...(labelKey ? { [labelKey]: 'Select all' } : {}),
    ...(valueKey ? { [valueKey]: SELECT_ALL_SENTINEL } : {}),
    __gpxSelectAll: true,
  }
})

const effectiveOptions = computed(() =>
  props.showSelectAll
    ? [sentinelOption.value, ...props.options]
    : props.options,
)

const mergedAttrs = computed(() => {
  const base = { ...attrs }
  if (props.showSelectAll) {
    base.showToggleAll = false
    base.panelClass = [base.panelClass, 'gpx-multiselect-overlay']
      .filter(Boolean)
      .join(' ')
  }
  return base
})

const forwardedSlots = computed(() => {
  const out = { ...slots }
  if (props.showSelectAll) delete out.option
  return out
})

const resolveOptionValue = (option) => {
  if (option == null) return option
  if (props.optionValue == null) return option
  if (typeof props.optionValue === 'function')
    return props.optionValue(option)
  return option?.[props.optionValue]
}

const resolveOptionLabel = (option) => {
  if (option == null) return ''
  if (typeof props.optionLabel === 'function')
    return props.optionLabel(option)
  return option?.[props.optionLabel] ?? ''
}

const isSentinel = (option) => option?.__gpxSelectAll === true

const realOptionValues = computed(() => props.options.map(resolveOptionValue))

const anySelected = computed(() => props.modelValue.length > 0)

const onUpdate = (newValue) => {
  if (newValue.includes(SELECT_ALL_SENTINEL)) {
    emit(
      'update:modelValue',
      anySelected.value ? [] : [...realOptionValues.value],
    )
    return
  }
  emit('update:modelValue', newValue)
}
</script>

<style>
/* Unscoped because the dropdown panel is teleported via Portal. We scope it
   to .gpx-multiselect-overlay (applied via panel-class) so it can't leak to
   other PrimeVue multi-selects. */
.gpx-multiselect-overlay
  .p-multiselect-option:has(.gpx-multiselect__select-all-row)
  .p-checkbox {
  display: none;
}

.gpx-multiselect-overlay .gpx-multiselect__select-all-row {
  font-weight: 500;
}
</style>
